/**
 * CreateTeamModal — Team workspaces (V2 additive).
 *
 * Two steps:
 *   1. Name the team.
 *   2. Configure the plan — billing period (weekly / yearly), total seats
 *      (2 included, extras priced per seat), and the WEEKLY shared credit pool
 *      (200 included; bigger tiers priced slightly below linear — the savings
 *      badge makes that explicit to encourage upsizing).
 * Then we create the org (status 'pending') and hand off to Stripe hosted
 * checkout; the webhook activates the org, and the return URL lands on /team.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Minus, Plus, Users, Loader2, ArrowRight } from 'lucide-react';
import { cn } from '@/v2/utils';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { orgService } from '@/api/orgService';
import { TEAM_PLAN, teamCreditTierSavings } from '@/api/config';
import { toast } from '@/v2/toast';

export function CreateTeamModal({ onClose, resumeOrg, initial, skipPlanStep }: {
  onClose: () => void;
  /** Resume checkout for an already-created org that never completed payment
   *  (skips the name step; no new org row is created). */
  resumeOrg?: { orgId: string; name: string };
  /** Pre-picked plan config (e.g. from the pricing page's Team card). */
  initial?: { billingPeriod: 'weekly' | 'yearly'; seats: number; credits: number };
  /** The caller already collected the plan config — only ask for the name,
   *  then go straight to checkout. */
  skipPlanStep?: boolean;
}) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg);

  const [step, setStep] = useState<1 | 2>(resumeOrg ? 2 : 1);
  const [name, setName] = useState(resumeOrg?.name || '');
  const [billing, setBilling] = useState<'weekly' | 'yearly'>(initial?.billingPeriod ?? 'yearly');
  const [seats, setSeats] = useState<number>(initial?.seats ?? TEAM_PLAN.includedSeats);
  const [credits, setCreditsTier] = useState<number>(initial?.credits ?? TEAM_PLAN.baseCredits);
  const [loading, setLoading] = useState(false);

  const extraSeats = Math.max(0, seats - TEAM_PLAN.includedSeats);
  const weeklyTotal =
    billing === 'weekly'
      ? TEAM_PLAN.weekly.creditTierTotals[credits] + extraSeats * TEAM_PLAN.weekly.seat
      : TEAM_PLAN.yearly.creditTierTotalsPerWeek[credits] + extraSeats * TEAM_PLAN.yearly.seatPerWeek;
  const yearlyTotal = TEAM_PLAN.yearly.creditTierTotals[credits] + extraSeats * TEAM_PLAN.yearly.seat;

  const handleCheckout = async () => {
    if (!userId || loading) return;
    setLoading(true);
    try {
      const orgId = resumeOrg
        ? resumeOrg.orgId
        : (await orgService.createOrg(userId, name.trim())).orgId;
      // Pre-select the new workspace so the Stripe return lands in team context.
      setActiveOrg(orgId);
      const session = await orgService.teamCheckout({
        userId,
        orgId,
        billingPeriod: billing,
        seats,
        credits,
        successUrl: `${window.location.origin}/team?checkout=success`,
        cancelUrl: `${window.location.origin}/team?checkout=canceled`,
      });
      if (session.checkout_url) {
        window.location.href = session.checkout_url;
      } else {
        toast.error(t('team.checkoutError'));
        setLoading(false);
      }
    } catch {
      toast.error(t('team.checkoutError'));
      setLoading(false);
    }
  };

  // PORTALED to document.body: ancestors with CSS transforms (animated
  // sidebar/header) would otherwise scope `position: fixed` to themselves and
  // the overlay wouldn't cover the whole screen.
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-surface border border-glass-border shadow-2xl animate-fade-in overflow-hidden">
        <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-glass-border">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/25 to-accent/25 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-text-primary">{t('team.createTeam')}</h2>
            <p className="text-xs text-text-tertiary">
              {skipPlanStep
                ? t('team.createStepNameOnly', { total: weeklyTotal })
                : step === 1 ? t('team.createStepName') : t('team.createStepPlan')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-light transition-colors" aria-label={t('team.close')}>
            <X className="w-5 h-5 text-text-tertiary" />
          </button>
        </div>

        {step === 1 && !resumeOrg ? (
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">{t('team.teamNameLabel')}</label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !name.trim()) return;
                  if (skipPlanStep) void handleCheckout(); else setStep(2);
                }}
                placeholder={t('team.teamNamePlaceholder')}
                maxLength={80}
                className="w-full px-4 py-3 rounded-xl bg-surface-light border border-glass-border focus:border-primary/60 focus:outline-none text-text-primary placeholder:text-text-tertiary"
              />
            </div>
            <p className="text-xs text-text-tertiary">{t('team.createIntro')}</p>
            <button
              disabled={!name.trim() || loading}
              onClick={() => { if (skipPlanStep) void handleCheckout(); else setStep(2); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white font-semibold disabled:opacity-40 transition-opacity"
            >
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <>{skipPlanStep ? t('team.continueToCheckout') : t('team.continue')} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Billing period */}
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-surface-light">
              {(['weekly', 'yearly'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setBilling(period)}
                  className={cn(
                    'py-2 rounded-lg text-sm font-semibold transition-colors',
                    billing === period ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  {period === 'weekly' ? t('team.billedWeekly') : t('team.billedYearly')}
                  {period === 'yearly' && (
                    <span className="ml-1.5 text-[10px] font-bold text-emerald-300">{t('team.yearlySave')}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Seats */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-surface-light border border-glass-border">
              <div>
                <p className="text-sm font-semibold text-text-primary">{t('team.seatsLabel')}</p>
                <p className="text-xs text-text-tertiary">
                  {t('team.seatsIncluded', { count: TEAM_PLAN.includedSeats })}
                  {extraSeats > 0 && (
                    <> · {t('team.extraSeatPrice', {
                      price: billing === 'weekly' ? TEAM_PLAN.weekly.seat : TEAM_PLAN.yearly.seatPerWeek,
                    })}</>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSeats((s) => Math.max(TEAM_PLAN.includedSeats, s - 1))}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  aria-label={t('team.fewerSeats')}
                >
                  <Minus className="w-4 h-4 text-text-secondary" />
                </button>
                <span className="w-8 text-center text-lg font-bold text-text-primary">{seats}</span>
                <button
                  onClick={() => setSeats((s) => Math.min(TEAM_PLAN.maxSeats, s + 1))}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  aria-label={t('team.moreSeats')}
                >
                  <Plus className="w-4 h-4 text-text-secondary" />
                </button>
              </div>
            </div>

            {/* Credit tier */}
            <div className="p-4 rounded-xl bg-surface-light border border-glass-border">
              <div className="flex items-baseline justify-between mb-3">
                <p className="text-sm font-semibold text-text-primary">{t('team.weeklyCreditsLabel')}</p>
                <p className="text-xs text-text-tertiary">{t('team.creditsShared')}</p>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {TEAM_PLAN.creditTiers.map((tier) => {
                  const savings = teamCreditTierSavings(tier, billing);
                  return (
                    <button
                      key={tier}
                      onClick={() => setCreditsTier(tier)}
                      className={cn(
                        'py-2 rounded-lg text-center border transition-all',
                        credits === tier
                          ? 'border-primary bg-primary/15 text-primary'
                          : 'border-glass-border text-text-secondary hover:border-primary/40'
                      )}
                    >
                      <span className="block text-sm font-bold">{tier}</span>
                      {savings > 0 && (
                        <span className="block text-[9px] font-semibold text-emerald-400">
                          {t('team.savePercent', { percent: savings })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2.5 text-[11px] text-text-tertiary">{t('team.creditTierHint')}</p>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between px-1">
              <span className="text-sm text-text-secondary">{t('team.total')}</span>
              <span className="text-right">
                <span className="text-xl font-extrabold text-text-primary">${weeklyTotal}</span>
                <span className="text-sm text-text-tertiary">{t('team.perWeek')}</span>
                {billing === 'yearly' && (
                  <span className="block text-[11px] text-text-tertiary">
                    {t('team.billedYearlyTotal', { amount: yearlyTotal })}
                  </span>
                )}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => (resumeOrg ? onClose() : setStep(1))}
                className="px-4 py-3 rounded-xl bg-surface-light text-text-secondary text-sm font-semibold hover:text-text-primary transition-colors"
              >
                {t('team.back')}
              </button>
              <button
                onClick={handleCheckout}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white font-semibold disabled:opacity-60 transition-opacity"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t('team.continueToCheckout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
